import { useEffect, useId, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import { FieldRow } from "@/components/ui/Field";
import { formatAuftragLookupLabel } from "@/features/globalUpload/formatAuftragLookupLabel";
import { getRecentAuftraege, searchAuftraege, type AuftragLookup } from "@/lib/material";
import { useFloatingPanel, estimateAutocompletePanelHeight, floatingPanelStyle } from "@/lib/useFloatingPanel";

export function AuftragMultiPicker({
  value,
  onChange,
  listboxZIndexClass = "z-[900]",
  singleSelect = false,
}: {
  value: AuftragLookup[];
  onChange: (next: AuftragLookup[]) => void;
  /** Über dem Upload-Modal (z-[70]), aber unter globalen Bestätigungsdialogen. */
  listboxZIndexClass?: string;
  /** Nur ein Projekt auswählbar (z. B. für Monteure). */
  singleSelect?: boolean;
}) {
  const id = useId();
  const triggerRef = useRef<HTMLDivElement | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [query, setQuery] = useState("");
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<AuftragLookup[]>([]);
  const [loading, setLoading] = useState(false);
  const closeTimer = useRef<number | null>(null);

  const selectedIds = useMemo(() => new Set(value.map((a) => a.id)), [value]);
  const panelHeightEstimate = useMemo(
    () =>
      estimateAutocompletePanelHeight({
        optionCount: options.length,
        loading,
        empty: !loading && options.length === 0,
        hasDescription: options.some((a) => Boolean(a.beschreibung)),
        maxHeightCap: 320,
      }),
    [loading, options],
  );
  const floating = useFloatingPanel(triggerRef, open, {
    gap: 4,
    maxHeightCap: 320,
    minBelow: 180,
    contentHeightEstimate: panelHeightEstimate,
  });

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const timer = window.setTimeout(async () => {
      setLoading(true);
      try {
        const list = query.trim()
          ? await searchAuftraege(query)
          : await getRecentAuftraege({ limit: 15 });
        if (!cancelled) {
          setOptions(list.filter((a) => !selectedIds.has(a.id)));
        }
      } catch {
        if (!cancelled) setOptions([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 200);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [open, query, selectedIds]);

  function add(auftrag: AuftragLookup) {
    if (selectedIds.has(auftrag.id)) return;
    onChange(singleSelect ? [auftrag] : [...value, auftrag]);
    setQuery("");
    if (singleSelect) setOpen(false);
  }

  function remove(auftragId: number) {
    onChange(value.filter((a) => a.id !== auftragId));
  }

  function scheduleClose() {
    if (closeTimer.current) window.clearTimeout(closeTimer.current);
    closeTimer.current = window.setTimeout(() => setOpen(false), 120);
  }

  return (
    <FieldRow
      label={singleSelect ? "Projekt auswählen" : "Projekte auswählen"}
      hint="Tippe zur Suche nach Projektnummer, Beschreibung oder Kunde."
      fieldId={id}
    >
      <div className="relative">
        <div
          ref={triggerRef}
          className="flex min-h-[42px] w-full flex-wrap items-center gap-1.5 rounded-lg border border-separator bg-card px-2 py-1 shadow-sm focus-within:border-accent focus-within:ring-2 focus-within:ring-accent-soft"
          onClick={() => {
            setOpen(true);
            inputRef.current?.focus();
          }}
        >
          {value.map((a) => (
            <span
              key={a.id}
              className="inline-flex max-w-full items-center gap-1 rounded-full bg-accent-soft px-2 py-0.5 text-xs font-medium text-accent"
            >
              <span className="truncate">{formatAuftragLookupLabel(a)}</span>
              <button
                type="button"
                aria-label={`${formatAuftragLookupLabel(a)} entfernen`}
                className="shrink-0 rounded-full p-0.5 text-accent hover:bg-fill-secondary"
                onClick={(e) => {
                  e.stopPropagation();
                  remove(a.id);
                }}
              >
                ×
              </button>
            </span>
          ))}
          <input
            ref={inputRef}
            id={id}
            type="text"
            className="min-w-[160px] flex-1 border-0 bg-transparent p-1 text-sm text-label placeholder:text-label-tertiary focus:outline-none focus:ring-0"
            placeholder={
              value.length === 0
                ? singleSelect
                  ? "Projekt suchen…"
                  : "Projekte suchen…"
                : ""
            }
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              setOpen(true);
            }}
            onFocus={() => setOpen(true)}
            onBlur={scheduleClose}
            onKeyDown={(e) => {
              if (e.key === "Backspace" && !query && value.length > 0) {
                e.preventDefault();
                onChange(value.slice(0, -1));
              }
              if (e.key === "Enter" && options[0]) {
                e.preventDefault();
                add(options[0]);
              }
              if (e.key === "Escape") setOpen(false);
            }}
          />
        </div>
        {open && floating && typeof document !== "undefined"
          ? createPortal(
              <ul
                id={`${id}-listbox`}
                role="listbox"
                className={clsx(
                  listboxZIndexClass,
                  "overflow-auto rounded-lg border border-separator bg-card py-1 text-sm shadow-ios-md",
                )}
                style={floatingPanelStyle(floating)}
                onMouseDown={(e) => e.preventDefault()}
              >
                {loading && <li className="px-3 py-2 text-label-tertiary">Suche …</li>}
                {!loading && options.length === 0 && (
                  <li className="px-3 py-2 text-label-tertiary">Keine Treffer …</li>
                )}
                {!loading &&
                  options.map((a) => (
                    <li key={a.id} role="option">
                      <button
                        type="button"
                        className="block w-full px-3 py-1.5 text-left text-label hover:bg-fill-tertiary"
                        onMouseDown={(e) => {
                          e.preventDefault();
                          add(a);
                        }}
                      >
                        <div className="font-medium">{formatAuftragLookupLabel(a)}</div>
                        {a.beschreibung ? (
                          <div className="text-xs text-label-secondary">{a.beschreibung}</div>
                        ) : null}
                      </button>
                    </li>
                  ))}
              </ul>,
              document.body,
            )
          : null}
      </div>
    </FieldRow>
  );
}
